import * as React from 'react';

import { cn } from '@rematter/ui-web';
import {
  UnstyledButton,
  OverlayArrow,
  RawTooltip,
  TooltipTrigger,
} from '@rematter/ui-web';
import { cva } from 'class-variance-authority';
import { ChevronDown } from 'lucide-react';
import { motion } from 'motion/react';
import { Link } from 'wouter';

import { useTranslations } from '~/shared/i18n/useTranslations';
import type { ModuleDefinition } from '~/shared/routing/types';
import { sortSubsectionsByOrder } from '~/shared/utils/sortNavItems';

// Constants for animation
const DELAY_INTERVAL = 0.05; // 50ms between each item
const MAX_ANIMATION_ITEMS = 9;

interface SidebarModuleItemProps {
  module: ModuleDefinition;
  isCollapsed: boolean;
  isLabelVisible: boolean;
  expandedModules: string[];
  location: string;
  toggleModule: (path: string) => void;
  subsectionOrder?: string[];
}

const SidebarModuleItem = ({
  module,
  isCollapsed,
  isLabelVisible,
  expandedModules,
  location,
  toggleModule,
  subsectionOrder,
}: SidebarModuleItemProps) => {
  const t = useTranslations();
  const { path, label, icon: Icon, nav: menu } = module;
  const isActive = location.startsWith(path);

  const subMenu = menu?.children;

  const [isTooltipOpen, setIsTooltipOpen] = React.useState(false);

  const visibleSubMenu = React.useMemo(() => {
    const visible = subMenu?.filter((item) => item.route.label) ?? [];
    if (subsectionOrder && subsectionOrder.length > 0) {
      return sortSubsectionsByOrder(visible, subsectionOrder);
    }
    return visible;
  }, [subMenu, subsectionOrder]);

  if (subMenu && subMenu.length > 0 && visibleSubMenu.length === 0) {
    return null;
  }

  const handleSubmenuClick = () => {
    setIsTooltipOpen(false);
  };

  const hasChildren = visibleSubMenu.length > 0;

  const buttonContent = (
    <>
      <span className="flex items-center">
        {Icon && <Icon className={iconVariants({ collapsed: false })} />}
        <span
          className={cn(
            'truncate transition-opacity duration-200',
            isLabelVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {label?.(t)}
        </span>
      </span>
      {hasChildren && !isCollapsed && (
        <ChevronDown
          className={cn(
            'size-4 transition-all duration-200',
            expandedModules.includes(path) ? 'rotate-180' : '',
            isLabelVisible ? 'opacity-100' : 'opacity-0',
          )}
        />
      )}
    </>
  );

  return (
    <TooltipTrigger
      isDisabled={!isCollapsed}
      delay={50}
      isOpen={isTooltipOpen}
      onOpenChange={setIsTooltipOpen}
    >
      {hasChildren ? (
        <UnstyledButton
          onClick={isCollapsed ? undefined : () => toggleModule(path)}
          className={moduleButtonVariants({
            active: isActive,
          })}
        >
          {buttonContent}
        </UnstyledButton>
      ) : (
        <Link
          to={path}
          className={moduleButtonVariants({
            active: isActive,
          })}
        >
          {buttonContent}
        </Link>
      )}
      {isCollapsed && hasChildren ? (
        <RawTooltip className="font-medium" placement="right">
          <OverlayArrow />
          <div className="min-w-48 p-2">
            <div className="mb-2 px-2 text-sm font-semibold">{label?.(t)}</div>
            {visibleSubMenu.map(({ route }) => (
              <Link
                key={route.path}
                to={route.path}
                className="flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-foreground/10"
                onClick={handleSubmenuClick}
              >
                {route.icon && <route.icon size={14} className="mr-2" />}
                {route.label?.(t)}
              </Link>
            ))}
          </div>
        </RawTooltip>
      ) : (
        <RawTooltip className="font-medium" placement="right">
          <OverlayArrow />
          {label?.(t)}
        </RawTooltip>
      )}
      {hasChildren && (
        <motion.div
          animate={{
            maxHeight:
              isLabelVisible && expandedModules.includes(path) ? 500 : 0,
            opacity: isLabelVisible && expandedModules.includes(path) ? 1 : 0,
          }}
          className={subRoutesListVariants({
            expanded: isLabelVisible && expandedModules.includes(path),
          })}
        >
          {visibleSubMenu.map(({ route }, index) => (
            <motion.div
              key={route.path}
              animate={{
                translateY:
                  isLabelVisible && expandedModules.includes(path) ? 0 : -10,
                opacity:
                  isLabelVisible && expandedModules.includes(path) ? 1 : 0,
              }}
              transition={{
                delay:
                  isLabelVisible && expandedModules.includes(path)
                    ? getAnimationDelay(index)[0]
                    : 0,
                duration: 0.2,
              }}
            >
              <Link
                to={route.path}
                className={subRouteLinkVariants({
                  active: location.startsWith(route.path),
                })}
              >
                <span className="flex items-center px-4">
                  {route.icon && (
                    <route.icon
                      className={iconVariants({ collapsed: false })}
                    />
                  )}
                  <span
                    className={cn(
                      'transition-opacity duration-200',
                      isLabelVisible ? 'opacity-100' : 'opacity-0',
                    )}
                  >
                    {route.label?.(t)}
                  </span>
                </span>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      )}
    </TooltipTrigger>
  );
};

/**
 * Calculate delay values for animation based on index and expansion state
 */
const getAnimationDelay = (index: number): [number, number] => {
  // Cap the index to prevent excessive delays
  const safeIndex = Math.min(index, MAX_ANIMATION_ITEMS);
  return [
    safeIndex * DELAY_INTERVAL,
    (MAX_ANIMATION_ITEMS - safeIndex) * DELAY_INTERVAL,
  ];
};

const iconVariants = cva('size-4 flex-shrink-0 transition-all', {
  variants: {
    collapsed: {
      true: '',
      false: 'mr-3',
    },
  },
  defaultVariants: {
    collapsed: false,
  },
});

const subRoutesListVariants = cva(['max-h-0 space-y-1 overflow-hidden pl-3'], {
  variants: {
    expanded: {
      true: 'mt-1',
    },
  },
});

const subRouteLinkVariants = cva(['block rounded-md py-2 text-sm font-light'], {
  variants: {
    active: {
      true: 'bg-neutral/10 font-semibold text-neutral/90',
      false: 'text-neutral/70 hover:bg-neutral/10 hover:text-neutral/90',
    },
  },
  defaultVariants: {
    active: false,
  },
});

const moduleButtonVariants = cva(
  'group flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-semibold transition-all duration-300',
  {
    variants: {
      active: {
        true: 'bg-neutral text-white',
        false: 'text-neutral/70 hover:bg-neutral/10 hover:text-neutral/90',
      },
    },
  },
);

export { iconVariants, moduleButtonVariants, SidebarModuleItem };
